/**
 * Demand and line-item notes (HS2-E33XQD, HS2-JRQT8A) — shared client↔server rules: the 60-second
 * author edit window, which list a note belongs to, and what counts as unread.
 */

/** How long after posting the author may still edit their most recent note in a list. */
export const NOTE_EDIT_WINDOW_MS = 60_000;

/** The longest note body accepted. */
export const NOTE_MAX_LENGTH = 5000;

/** The scope key of the demand-level notes list; a line's list uses the line item id. */
export const DEMAND_NOTES_SCOPE = 'demand';

/** A note as the notes APIs serve it. Timestamps are ISO strings. */
export interface DemandNote {
  id: string;
  lineItemId: string | null;
  authorMemberId: string | null;
  authorName: string | null;
  body: string;
  createdAt: string;
  editedAt: string | null;
}

/** The scope key a note's list is read and counted under. */
export function noteScopeKey(lineItemId: string | null | undefined): string {
  return lineItemId ?? DEMAND_NOTES_SCOPE;
}

/** The notes of one list (`lineItemId` null for the demand's own list), oldest first. */
export function notesInScope(notes: readonly DemandNote[], lineItemId: string | null): DemandNote[] {
  return notes
    .filter((note) => (note.lineItemId ?? null) === lineItemId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/**
 * Whether `memberId` may still edit `note` at `now`: they wrote it, it is the newest note in its
 * list, and it was posted under a minute ago.
 */
export function canEditNote(
  note: DemandNote,
  listNotes: readonly DemandNote[],
  memberId: string | null | undefined,
  now: number,
): boolean {
  if (memberId === null || memberId === undefined || note.authorMemberId !== memberId) return false;
  const newest = listNotes.at(-1);
  if (newest?.id !== note.id) return false;
  return now - Date.parse(note.createdAt) < NOTE_EDIT_WINDOW_MS;
}

/** Notes in a list that `memberId` has not read: by someone else, posted after their last read. */
export function unreadNotes(
  listNotes: readonly DemandNote[],
  readAt: string | null | undefined,
  memberId: string | null | undefined,
): DemandNote[] {
  return listNotes.filter(
    (note) =>
      note.authorMemberId !== memberId &&
      (readAt === null || readAt === undefined || note.createdAt.localeCompare(readAt) > 0),
  );
}
